import {
  ReactNode,
  createContext,
  useMemo,
  useState,
  useCallback,
} from "react";
import { jwtDecode } from "jwt-decode";
import { usePermissionsContext } from "./PermissionsContext";
import Cookies from "js-cookie";
import { IDataLojas } from "../types/api/geral/pesquisar-lojas";

export const AuthContext = createContext<DrawerContextType>({
  isAuth: false,
  updateToken: () => {},
  logout: () => null,
  getUserData: () => null,
  getUserCooperativaData: () => null,
  token: "",
  tokenDecoded: null,
  possuiPerfilId: () => false,
  userPerfilComLoja: null
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const { procurarPermissoes, removerPermissoes } = usePermissionsContext();

  const [token, setToken] = useState<string | null>(
    Cookies.get("token") || null
  );

  const tokenDecoded = token ? jwtDecode<tokenDecoded>(token) : null;

  const isAuth = token ? true : false;

  const logout = () => {
    removerPermissoes();
    Cookies.remove("token");
    setToken(null);
  };

  const updateToken = (token: string) => {
    const expirationData = getExpirationDate();
    Cookies.set("token", token, { expires: expirationData });
    setToken(token);
    procurarPermissoes();
  };

  const getUserData = useCallback(() => {
    if (token) {
      let decoded: any = jwtDecode(token);
      return {
        uuid: decoded.usuario?.uuid,
        nome: decoded.usuario?.nome,
        perfis_vinculados: decoded.perfis_vinculados,
      };
    }
    return null;
  }, [token]);

  const getUserCooperativaData = () => {
    if (token) {
      let decoded: any = jwtDecode(token);
      return {
        cooperativa_uuid: decoded.cooperativa?.uuid,
      };
    }

    return null;
  };

  const possuiPerfilId = (perfilId: number | null | undefined) => {
    if (perfilId === null || perfilId === undefined) return false;
    const perfisVinculados = getUserData()?.perfis_vinculados || [];
    return perfisVinculados.some(
      (perfil: UserDataPerfil) => perfil.perfil_id === perfilId
    );
  };

  const userPerfilComLoja = useMemo(() => {
    const perfisVinculados = getUserData()?.perfis_vinculados || [];
    const perfilComLoja = perfisVinculados.find(
      (perfil: UserDataPerfil) => perfil.loja
    );
    return perfilComLoja || perfisVinculados[0] || null;
  }, [getUserData]);

  return (
    <AuthContext.Provider
      value={{
        isAuth,
        updateToken,
        logout,
        getUserData,
        getUserCooperativaData,
        token,
        tokenDecoded,
        possuiPerfilId,
        userPerfilComLoja
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

const getExpirationDate = () => {
  const expirationDate = new Date();
  expirationDate.setHours(23, 59, 59, 999);

  return expirationDate;
};

export interface UserData {
  uuid: string;
  nome: string;
  perfis_vinculados: Array<UserDataPerfil>;
}
export interface UserCooperativaData {
  cooperativa_uuid: string;
}

export interface UserDataPerfil {
  id: number;
  nome: string;
  loja: IDataLojas | null;
  vendedor: ObjectType | null;
  perfil_id: number;
  condicoes_pagamento_blocked: number[] | null;
  limite_alcada: number | null;
}

type ObjectType = {
  id: number;
  nome: string;
};

type tokenDecoded = {
  cooperativa: {
      uuid: string;
  };
  usuario: {
      uuid: string;
      email: string;
      nome: string;
  };
  perfis_vinculados: Array<{
      id: number;
      tipo_publico: string[];
  }>;
}
export interface DrawerContextType {
  isAuth: boolean;
  updateToken: (token: string) => void;
  logout: () => void;
  getUserData: () => UserData | null;
  getUserCooperativaData: () => UserCooperativaData | null;
  token: string | null;
  tokenDecoded: tokenDecoded | null;
  possuiPerfilId: (perfilId: number | null | undefined) => boolean;
  userPerfilComLoja: UserDataPerfil | null;
}
